import React from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";
import ItemsPerPageDropdown from "../dropdown/ItemsPerPageDropdown";
import { buildPageList, DEFAULT_TABLE_PAGE_SIZE } from "./tableUtils";
import "./table.css";

function TablePagination({
  page = 1,
  pageSize = DEFAULT_TABLE_PAGE_SIZE,
  total = 0,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [DEFAULT_TABLE_PAGE_SIZE, 20, 25, 50],
  showLabel = "Show",
  ofLabel = "Of",
  resultsLabel = "results",
  isRtl = false,
  previousLabel = "Previous",
  nextLabel = "Next",
}) {
  const { i18n } = useTranslation();
  const rtl = Boolean(isRtl || i18n.dir() === "rtl");
  const safeTotal = Math.max(0, Number(total) || 0);
  const safePageSize = Math.max(1, Number(pageSize) || DEFAULT_TABLE_PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(safeTotal / safePageSize));
  const currentPage = Math.min(Math.max(1, Number(page) || 1), totalPages);
  const pageItems = buildPageList(currentPage, totalPages);
  const PrevIcon = rtl ? ChevronRight : ChevronLeft;
  const NextIcon = rtl ? ChevronLeft : ChevronRight;

  return (
    <div className="oh-table-footer" dir={rtl ? "rtl" : "ltr"}>
      <div className="oh-table-footer__left">
        <span>{showLabel}</span>
        <ItemsPerPageDropdown
          className="oh-table-page-size"
          itemsPerPage={safePageSize}
          onChange={(n) => onPageSizeChange?.(n)}
          height="24px"
          numericLabelOnly
          options={pageSizeOptions}
        />
        <span>
          {ofLabel} {safeTotal} {resultsLabel}
        </span>
      </div>
      <div className="oh-table-footer__nav">
        <button
          type="button"
          className="oh-table-page-btn"
          disabled={currentPage <= 1}
          aria-label={previousLabel}
          onClick={() => onPageChange?.(Math.max(1, currentPage - 1))}
        >
          <PrevIcon size={14} strokeWidth={2} aria-hidden="true" />
        </button>
        <div className="oh-table-footer__pages">
          {pageItems.map((item, idx) =>
            item === "…" ? (
              <span key={`ellipsis-${idx}`} className="oh-table-page-ellipsis">
                …
              </span>
            ) : (
              <button
                key={`p-${item}`}
                type="button"
                className={`oh-table-page-btn${item === currentPage ? " is-active" : ""}`}
                aria-current={item === currentPage ? "page" : undefined}
                onClick={() => onPageChange?.(item)}
              >
                {item}
              </button>
            )
          )}
        </div>
        <button
          type="button"
          className="oh-table-page-btn"
          disabled={currentPage >= totalPages}
          aria-label={nextLabel}
          onClick={() => onPageChange?.(Math.min(totalPages, currentPage + 1))}
        >
          <NextIcon size={14} strokeWidth={2} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

export default TablePagination;
